import { userAPI } from '../services/api';

export const SITE_ROLE_ELIGIBLE = ['staff', 'co-admin', 'agent', 'technical'];
export const SECONDARY_ROLE_OPTIONS = ['staff', 'co-admin', 'agent', 'technical'];
export const ROLE_LABEL = { admin: 'Admin', 'co-admin': 'Co-Admin', agent: 'Agent', technical: 'Technical', staff: 'Staff' };
export const ROLE_HELP = {
  admin: 'Full access to everything. Not limited by any Site Role.',
  'co-admin': 'Works like staff, rights come from a Site Role.',
  staff: 'Office login, rights come from a Site Role.',
  technical: 'Field technician app, rights come from a Site Role.',
  agent: 'Collection agent app, rights are the Field Rights ticked below.',
};

// The save sequence for the Add/Edit user form. Moved here unchanged from UsersPage so the
// desktop modal and the mobile form call the same endpoints in the same order.
export async function saveUser({ user, form, fieldRights }) {
  const { siteRoleId, siteRole, ...rest } = form;
  const effectiveRoles = [form.role, ...form.secondaryRoles];
  const needsFieldRights = effectiveRoles.includes('agent');
  const needsSiteRole = effectiveRoles.some(r => SITE_ROLE_ELIGIBLE.includes(r) && r !== 'agent');
  if (user?._id) {
    await userAPI.update(user._id, rest);
    // Base role is a separate, adminOnly endpoint (backend strips `role` out of the
    // generic edit to stop self-escalation), only call it when it actually changed.
    if (form.role !== user.role) await userAPI.updateRole(user._id, form.role);
    const beforeSecondary = user.secondaryRoles || [];
    if (JSON.stringify([...form.secondaryRoles].sort()) !== JSON.stringify([...beforeSecondary].sort())) {
      await userAPI.updateSecondaryRoles(user._id, form.secondaryRoles);
    }
    if (needsFieldRights) await userAPI.updateFieldRights(user._id, fieldRights);
    if (needsSiteRole) {
      // Only touch the assignment if it actually changed (keeps the Change Ledger clean).
      const before = user.siteRole?._id || user.siteRole || '';
      if (siteRoleId !== before) await userAPI.updateSiteRole(user._id, siteRoleId || null);
    }
  } else {
    const created = await userAPI.create({ ...rest, siteRole: needsSiteRole ? (siteRoleId || undefined) : undefined });
    if (form.secondaryRoles.length) await userAPI.updateSecondaryRoles(created.data.data._id, form.secondaryRoles);
    if (needsFieldRights) await userAPI.updateFieldRights(created.data.data._id, fieldRights);
  }
}

export const grantedFieldRights = (u) =>
  Object.values(u?.fieldRights || {}).filter(m => Object.values(m || {}).some(Boolean)).length;

// Search by name, mobile or email (the login id); filter by base role and account status.
export function filterUsers(users, { search = '', role = 'all', status = 'all' } = {}) {
  const q = search.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  return users.filter(u => {
    if (role !== 'all' && u.role !== role) return false;
    if (status === 'active' && !u.isActive) return false;
    if (status === 'inactive' && u.isActive) return false;
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (!!digits && (u.mobile || '').replace(/\D/g, '').includes(digits));
  });
}

// Classifies a registry action key so the role editor can group and colour by kind.
// Display only: the keys themselves, and what they gate, are untouched.
export function actionKind(key) {
  if (/^view/i.test(key)) return 'view';
  if (/permanentDelete|bulkDelete|^delete|deactivate/i.test(key)) return 'delete';
  if (/^add|^create|^generate/i.test(key)) return 'create';
  if (/^edit|^update|^change|^assign|^accept|^transfer/i.test(key)) return 'edit';
  return 'special';
}
export const KIND_META = {
  view:    { label: 'View',   color: '#2563eb' },
  create:  { label: 'Create', color: '#059669' },
  edit:    { label: 'Edit',   color: '#d97706' },
  delete:  { label: 'Delete', color: '#dc2626' },
  special: { label: 'Admin',  color: '#7c3aed' },
};

// Rights that hand over control of other people's access or accounts. Granting one of these
// in a role asks for an extra confirmation.
export const CRITICAL_RIGHTS = [
  ['usersRoles', 'manageRoles'], ['usersRoles', 'loginAs'], ['usersRoles', 'permanentDelete'],
  ['usersRoles', 'assignSiteRole'], ['usersRoles', 'add'], ['usersRoles', 'edit'], ['usersRoles', 'deactivate'],
];
export const isCritical = (moduleKey, actionKey) => CRITICAL_RIGHTS.some(([m, a]) => m === moduleKey && a === actionKey);

// "3 of 8" style counts for one module / a whole rights object, ignoring the per-agent
// FIELD AGENT APP section (it is not part of Site Roles).
export function countRights(rights, registry) {
  let on = 0; let total = 0;
  for (const mod of registry) {
    if (mod.section === 'FIELD AGENT APP') continue;
    for (const a of mod.actions) { total += 1; if (rights?.[mod.key]?.[a.key]) on += 1; }
  }
  return { on, total };
}

// Critical rights that `next` grants and `prev` did not.
export function newlyGrantedCritical(prev, next) {
  return CRITICAL_RIGHTS.filter(([m, a]) => next?.[m]?.[a] && !prev?.[m]?.[a]).map(([m, a]) => `${m}.${a}`);
}
